import type { Chat, ChatStream, DataRequirements, QueryPlan } from './plan';
import { localToday } from './plan';
import { PLAIN_WRITING } from './writing';

/** 답변에 근거를 붙이는 데 필요한 것은 조회 계획뿐이다. */
type RetrievePlan = Extract<QueryPlan, { kind: 'retrieve' }>;
import { capTable, TableData } from './files';
import type { FileFormat, ReportDocument, ResultSnapshot } from './files';
import type { ExperienceCandidate } from './experience';

export type Column = {
  id: string;
  text?: string;
  concept?: string;
  /** 칸이 화면에 글자로 바꿔 보이는 코드값. 카탈로그가 `render` 에서 읽었다. */
  codes?: { value: string; label: string }[];
};
export type Shape = {
  label: string;
  columns: Column[];
  rowCount: number;
  /** `blank` — 행은 왔는데 화면이 이 칸을 못 채웠다(비었거나, 칸이 전부 0). 계산은 행에서 센다. */
  scalars?: { key: string; label: string; blank?: boolean }[];
  /**
   * 이 행을 가져온 조회 조건(`gijun=0(귀속년월), stmon=202610`). 계산은 조건을 몰라서, 귀속년월로
   * 이미 걸린 지로 606행을 매출일자로 또 걸러 판마다 601·606 을 오갔다(2026-10-12).
   */
  conditions?: string;
};
export type Answer = {
  text: string;
  code: string;
  /** 답변 끝에 붙어 사용자가 읽는 근거. 화면 이름과 사람이 읽는 조건이다. */
  sources: string[];
  /** 조회한 화면 경로. 다음 질문의 후보를 되살리는 데 쓴다 — 사용자에게 보이지 않는다. */
  routes?: string[];
  table?: TableData;
  formats?: FileFormat[];
  result?: ResultSnapshot;
  report?: ReportDocument;
  /** 조회 중에 떠서 닫은 알림들. 사용자는 못 본 것이다. */
  notices?: string[];
  /** 검증을 통과한 실행만 다음 질문의 경험 후보가 된다. 행 값은 포함하지 않는다. */
  experience?: ExperienceCandidate;
  task?: import('./task').TaskSpec;
  /** 서버에 맡겨 나중에 끝나는 일. 패널이 끝을 지켜보고 알린다. */
  queued?: import('./queued').QueuedJob[];
  /** 답 아래에 내미는 선택 단추. 누르면 그 글이 다음 질문이 된다(되묻기의 선택지와 같다). */
  options?: string[];
};
type Retrieved = { label: string; rows: Record<string, unknown>[]; totals?: Record<string, unknown> };

/**
 * 조회 결과에서 **모양만** 뽑는다.
 *
 * 값이 LLM 으로 나가면 계산을 브라우저 안에 두기로 한 뜻이 없어진다. 코드를
 * 쓰는 데 필요한 것은 컬럼 이름과 행 수뿐이다.
 *
 * 카탈로그가 그 표의 컬럼을 알면 그것을 쓴다. 행에서 키를 그냥 긁으면 화면이
 * 쓰지 않는 칸까지 딸려 온다 — 월매출매입현황은 4개를 보여주는데 행에는 76개가
 * 있고 그중 misamt01~12 는 전부 빈 문자열이다. 그것을 본 모델이 빈 칸을 골라
 * 그럴듯한 숫자를 지어냈다. 한글 라벨이 무엇을 뜻하는지도 알려준다.
 */
/**
 * **행은 왔는데 화면이 합계·건수 칸을 못 채웠으면** 그 칸에 표시한다. 점검결과등록 10월은 목록 12행이 왔는데 건수
 * 칸이 비어 「자료가 없어 진행률을 확인할 수 없습니다」로 답했다 — 계산이 「totals 를 써라」만 따랐다(사용자
 * 2026-10-15, 앱 버그). 빈 칸, 그리고 행이 있는데 칸이 **전부** 0 이면 못 채운 것으로 본다.
 */
export function markBlankTotals(
  scalars: { key: string; label: string }[] | undefined,
  totals: Record<string, unknown> | undefined,
  rowCount: number,
): { key: string; label: string; blank?: boolean }[] | undefined {
  if (!scalars?.length || rowCount === 0) return scalars;
  const text = (key: string) => String(totals?.[key] ?? '').replace(/[,\s원건%]/g, '').trim();
  const allZero = scalars.every((one) => /^0*(\.0+)?$/.test(text(one.key)));
  return scalars.map((one) => (!text(one.key) || allZero ? { ...one, blank: true } : one));
}

export function shapesOf(
  retrieved: Record<string, Retrieved>,
  declared: Record<string, Column[]> = {},
  declaredScalars: Record<string, { key: string; label: string }[]> = {},
): Shape[] {
  // **이름은 사전의 키에서 읽는다.** 결과에 실린 `label` 은 드라이버에 보낸 이름이라 다시 조회하면
  // 꼬리가 붙는다(`__조건뺌`·`__wide`). 행은 키 이름으로 넘어가는데 모델은 꼬리 붙은 이름을 보고
  // `rows['이번 달 지로 발행 내역__조건뺌']` 을 읽다 KeyError 로 죽었다(지로일괄발행, 2026-10-12).
  return Object.entries(retrieved).map(([label, result]) => {
    const columns: Column[] = declared[label]?.length
      ? (declared[label] as Column[])
      : Object.keys(result.rows[0] ?? {}).map((id) => ({ id }));
    // 열이 있어도 값이 하나도 없는 일이 있다. 월별입금현황의 `iamt(년합계)` 가 그랬다 —
    // 파이썬이 그것을 더해 수금이 0 원이라고 답했다. 월별 값은 다 있었는데.
    // 비었다는 사실을 적어 주면 다른 열로 계산한다.
    const empty = new Set(columns
      .map((column) => column.id)
      .filter((id) => result.rows.length > 0
        && !result.rows.some((row) => {
          const cell = row[id];
          return cell !== null && cell !== undefined && String(cell).trim() !== '';
        })));
    return {
      label,
      columns: columns.map((column) => {
        if (empty.has(column.id)) return { ...column, text: `${column.text ?? column.id}, 값 없음` };
        const shape = periodShape(result.rows, column.id);
        return shape ? { ...column, text: `${column.text ?? column.id}, 예: ${shape}` } : column;
      }),
      rowCount: result.rows.length,
      scalars: markBlankTotals(declaredScalars[label], result.totals, result.rows.length),
    };
  });
}

/**
 * 기간 열의 **생김새**를 한 예로 보여 준다.
 *
 * 계산은 컬럼 이름만 본다. `rtmon(발생월)` 이 `202608` 인 줄 모르고 `2026-08` 로
 * 걸러 **입사 0명, 퇴사 0명**을 답한 턴이 있었다 — 열은 맞게 집었는데 값이 안 맞았다.
 *
 * 사람의 자료는 여전히 나가지 않는다. 여섯·여덟 자리 **숫자만** 있는 열, 즉 YYYYMM
 * 이나 YYYYMMDD 로만 보이는 열에서만 한 개를 보인다. 이름·전화번호·주소는 이 잣대를
 * 통과하지 못한다.
 */
function periodShape(rows: Record<string, unknown>[], id: string): string | undefined {
  const values = rows
    .map((row) => String(row[id] ?? '').trim())
    .filter((value) => value !== '');
  if (values.length < 2) return undefined;
  if (!values.every((value) => /^(?:\d{6}|\d{8})$/.test(value))) return undefined;
  if (new Set(values.map((value) => value.length)).size !== 1) return undefined;
  return values[0];
}

/**
 * 코드 뜻을 아는 칸은 계산에 넘기기 전에 **우리가** 글자로 바꾼다.
 *
 * 「코드값 1=계획, 2=완료」를 알려 주고 바꾸기는 모델에게 맡겼더니 판마다 달랐다 — 어떤 판은 바꿔 셌고,
 * 어떤 판은 `state == '완료'` 로 걸러 「완료 0·계획 0·기타 7,017」을 냈다(「요새 점검 문제없어?」, 실제 테넌트,
 * 2026-10-23). 사전에 없는 값은 그대로 둔다. 바꾼 칸은 「값: 계획·완료」로 알리고 코드 사전은 뗀다 —
 * 남겨 두면 모델이 다시 코드로 거른다.
 */
export function decodeRows(
  rows: Record<string, unknown>[],
  columns: Column[],
): { rows: Record<string, unknown>[]; columns: Column[] } {
  const coded = columns.filter((column) => column.codes?.length);
  if (!coded.length) return { rows, columns };
  const meanings = new Map(coded.map((column) => [
    column.id, new Map((column.codes ?? []).map((code) => [String(code.value).trim(), code.label])),
  ] as const));
  // 사전에 없는 값(빈 칸 포함)도 몇 행인지 센다 — 고객싸인 사전은 「있음」 하나라 「값: 있음」만 알렸더니, 안 받은 행이
  // 빈 칸인 줄 모르고 다른 표시를 찾아 「서명 누락 0건」을 냈다(2026-10-25 09:14, 같은 칸이 09:03 판에는 1,807건).
  const others = new Map(coded.map((column) => [column.id, new Map<string, number>()] as const));
  const decoded = rows.map((row) => {
    const copy = { ...row };
    meanings.forEach((meaning, id) => {
      const raw = String(row[id] ?? '').trim();
      const label = meaning.get(raw);
      if (label !== undefined && label !== '') copy[id] = label;
      else if (!Array.from(meaning.values()).includes(raw)) others.get(id)?.set(raw, (others.get(id)?.get(raw) ?? 0) + 1);
    });
    return copy;
  });
  return {
    rows: decoded,
    columns: columns.map((column) => {
      if (!column.codes?.length) return column;
      const { codes, ...rest } = column;
      const left = Array.from(others.get(column.id)?.entries() ?? []).sort((one, two) => two[1] - one[1]).slice(0, 3)
        .map(([value, count]) => `${value ? value.slice(0, 10) : '빈 칸'} ${count.toLocaleString('en-US')}행`);
      return {
        ...rest,
        text: `${column.text ?? column.id}, 값: ${codes.map((code) => code.label).join('·')}${left.length ? `, 그 밖의 값: ${left.join('·')}` : ''}`,
      };
    }),
  };
}

/**
 * 열이 **무엇을 담는지** 한 마디. 이름만으로는 모르는 것만 적는다.
 *
 * 계산은 열 이름과 모양만 보고 코드를 쓴다. 이름은 「상태」인데 값은 `01`·`02` 라
 * 화면 글자(정상·휴업)로 거르면 하나도 안 걸리고, 「No」가 순번인 줄 모르면 그것을
 * 더한다. 열 의미를 붙이면 스키마만 줄 때보다 정확도가 크게 오른다는 측정이 있다
 * (45~50% → 68%, arXiv 2604.25149). 카탈로그는 이미 열마다 `concept` 를 알고, 코드
 * 칸은 화면이 글자로 바꾸는 사전(`codes`)을 안다 — 여기까지 와서 버리고 있었다.
 */
export function columnMeaning(column: Column): string {
  if (column.codes?.length && !/=/.test(column.text ?? '')) {
    return `코드값 ${column.codes.map((code) => `${code.value}=${code.label}`).join(', ')}`;
  }
  const shown = column.text ?? '';
  switch (column.concept) {
    case 'money': return '금액(원)';
    case 'count': return '수량';
    case 'rate': return '비율';
    case 'seq': return '화면 순번, 계산에 쓰지 마라';
    case 'note': return '자유 기재 문장';
    case 'date': return /예:/.test(shown) ? '' : '날짜, 여덟 자리';
    case 'month': return /예:/.test(shown) ? '' : '연월, 여섯 자리';
    // 상태·구분 칸은 코드일 때도, 글자(`당좌예금`)일 때도 있다. 사전이 없으면 짐작해
    // 적지 않는다 — 「코드값」이라 했다가 글자 칸을 못 거르게 만들면 더 나쁘다.
    default: return '';
  }
}

export const CODE_PROMPT = `조회 결과로 질문에 답할 값을 계산하는 파이썬을 쓴다.

- 데이터는 \`rows\` 라는 딕셔너리로 주어진다. 키는 아래 label 이고 값은 행 목록이다
- 마지막 줄이 결과다. 딕셔너리로 내면 답변에 쓰기 좋다
- 함수를 정의해도 되고 여러 줄을 써도 된다
- 숫자는 문자열로 들어오고 글자가 섞인 행도 있다('미처리 : 건'). **int()·float() 대신 준비된 num(값) 을 써라** —
  쉼표·공백·'원'·'%' 를 걷고, 숫자가 아니면 0 을 돌려준다. 누락으로 다뤄야 하면 num(값, None)
- **모듈을 import 하지 마라.** 표준 자료형과 내장 함수로 충분하다. 샌드박스가 막는 모듈이 있고, xml·csv 같은 것을 부르면 그것들이 막힌 모듈을 끌고 와 계산이 통째로 죽는다
- **아래 적힌 컬럼만 있다.** 없는 컬럼을 지어내지 마라
- "값 없음" 이라고 적힌 컬럼은 행이 있어도 값이 전부 비어 있다. 그것으로 합계를 내지 말고
  같은 뜻을 담은 다른 컬럼으로 계산하라 — 예를 들어 년합계가 비어 있으면 월별 값을 더한다
- 찾는 것이 데이터에 없으면 숫자를 만들지 말고 없다고 결과에 적어라
- **이름으로 거를 때는 글자가 똑같기를 요구하지 마라.** 사람은 \`덕봉빌딩\` 이라 말하고 화면은
  \`덕봉빌딩(두암정철)\` 을 담는다. 한쪽이 다른 쪽을 담고 있으면 같은 것으로 본다 —
  \`==\` 로 걸렀다가 2행을 읽고도 "자료 없음" 으로 답한 적이 있다
- **말한 낱말이 어느 컬럼에도 없으면 그것으로 거르지 마라.** \`소음 민원\` 의 \`소음\` 은
  민원처리보고서 표에 아예 없는 값이다. 없는 조건으로 거르면 전부 사라진다 —
  조회 조건이 이미 좁혀 놓은 행이 답이다
- **날짜 열은 여덟 자리다**(20260814). 달로 거를 때는 앞 여섯 자리를 견준다 —
  row["outdate"][:6] == "202608" 처럼. 여덟 자리 값을 "202608" 과 통째로 비교하면
  하나도 안 걸려 두 건을 읽고도 0 이 나온다
- **상태·구분 열에는 코드가 들어 있다.** 상태가 "2" 로 오지 "완료" 로 오지 않는다.
  화면에 보이는 글자와 비교하면 하나도 안 걸린다 — 두 건을 읽고도 0건이라 답했다.
  컬럼 설명에 \`코드값 01=정상, 02=휴업\` 처럼 사전이 있으면 사람 말을 그 코드로 바꿔 거른다.
  코드가 무엇을 뜻하는지 모르면 **그 열로 거르지 마라.** 조회 조건이 이미 걸러 놓은
  행을 그대로 세는 것이 맞다. 이름이 필요하면 짝이 되는 이름 열(…nm)을 쓴다
- 화면 행의 현재 순서를 순위로 믿지 마라. 필터·여러 조회 결합·그룹합계·순위·정렬은 이 파이썬에서 직접 한다
- 분류 칸(현장명·담당자명 등)이 빈 행도 **실제 자료다.** 코드가 없는 입금이 그렇게 들어온다.
  합계에서 빼지 마라 — 화면 합계도 그 행을 포함한다. 분류별로 나눌 때만 "미지정" 으로 묶는다
- 표에 합계 행이 따로 있는 화면도 있다. 그것은 **나머지 행의 합과 같은** 행이다. 값을 비교해
  확인되면 빼고, 확인되지 않으면 빼지 마라
- 같은 기간을 여러 label로 나눠 조회했다면 필요한 행을 합친 뒤 질문의 분류축으로 그룹화한다
- **행은 「조회 조건」으로 이미 걸러져 왔다.** 질문의 기간이 그 조건과 같으면 다른 날짜 열로 다시
  거르지 마라 — 화면을 본 사람은 걸러진 행 전부를 본다. 지로를 귀속년월 10월로 조회했는데 매출일자가
  11월인 행이 다섯 있었고, 그것을 빼서 판마다 건수가 달랐다. 날짜 열이 그 기간 밖인 행은 빼지 말고
  몇 건인지 따로 적어라
- **질문이 가리키는 기간으로 걸러라.** 화면이 연간 표를 주더라도 이번 주·이번 달을 물었으면
  그 기간의 값이 핵심이다. 기간 열의 "예:" 형식을 보고 맞춰 거른다 —
  "발생월, 예: 202608" 이면 202608 로 비교하지 2026-08 로 하지 않는다
- 그 기간의 값을 낼 수 없으면 **무엇을 대신 냈는지 이름에 적어라** — "연간 누계",
  "8월 누계" 처럼. 주간을 물었는데 이름 없이 연간 합계를 내밀지 마라
- **어느 것이 많은지·누가·어디인지 물으면 합계만 내지 마라.** 이름과 값을 담은 목록을 함께 낸다 —
  "미수금이 많은 현장 알려줘" 에 "73곳, 합계 78억" 만 답하면 질문에 답한 것이 아니다.
  result 에 {"summary": ..., "table": {"columns": [...], "rows": [[...]]}} 로 담고 요청한 기준으로 정렬한다
- **물은 수치는 표가 아니라 결과에 담는다.** \`{"table": …}\` 만 내면 답변은 그 숫자를
  못 본다 — "접수 건수와 완료 건수는 아래 표에서 확인해 주세요" 로 나갔다.
  건수·금액을 물었으면 \`{"접수": 4, "완료": 2, "table": …}\` 처럼 수치를 함께 낸다
- **여러 행의 목록은 반드시 "table" 로 낸다.** summary 에는 건수·합계·그룹 집계·앞쪽 최대 10건만
  담는다(글자로 2,000자 안). summary 에 목록을 통째로 넣으면 답변은 앞부분만 보고 "목록이 끊겼다" 고 쓴다.
  옳다: {"summary": {"건수": 2035, "현장별 건수 상위": {...10개}}, "table": {"columns": ["현장명", "호기"], "rows": [[...] 전부]}}
  틀리다: {"summary": {"건수": 2035, "현장": [2035개 전부]}}
- 결과가 1,000건을 넘으면 summary 에 질문과 표에 맞는 분류 칸 하나(담당자·현장·부서·상태 등)로 묶은 건수 상위 5~10개를 넣는다
- 계산식은 질문과 데이터 계약에 명시된 원천 컬럼으로만 계산한다
- **질문의 말이 칸에 그대로 없어 다른 칸으로 판정했으면**(「경력직」을 경력 기록이 있는지로) 결과에
  "판정기준" 키로 무엇을 무엇으로 셌는지 한 문장을 담아라. 답변은 그 문장으로 기준을 밝힌다
- 인터넷·파일에 접근할 수 없다. 표준 라이브러리만 쓴다

파이썬 코드만 출력한다. 설명을 붙이지 마라.

질문: {question}

데이터 계약:
{requirements}

가진 데이터:
{shapes}`;

/**
 * 파일을 요청받았을 때만 코드 프롬프트 끝에 붙는다.
 *
 * 평소에는 한 글자도 달라지지 않는다 — 화면 선택 평가의 기준선이 흔들리면
 * 무엇 때문에 달라졌는지 가릴 수 없다.
 */
export const FILE_PROMPT = `

사용자가 파일을 요청했다. 마지막 줄을 이렇게 낸다:
{"summary": <답변에 쓸 짧은 요약>, "table": {"columns": [...], "rows": [[...]]}}

- columns 는 사람이 읽을 이름이다 ("매출금액", "misamt" 아님)
- rows 는 조회한 것을 질문에 맞게 가공한 결과다. 원본을 그대로 옮기지 마라
- 순위 요청이면 rows 자체를 요청한 기준으로 정렬한다. 화면의 원래 순서에 기대지 마라
- 표를 만들 수 없으면 "table" 을 넣지 마라. 지어내지 마라`;

/**
 * **기준일 전후 비교의 지침.** 「알림을 10/15 에 넣은 뒤로 출퇴근을 더 잘 찍나」 같은 질문이다 — 기간을 어디서
 * 자르고, 한쪽이 며칠뿐이면 어떻게 말하고, 차이를 원인으로 말해도 되는지를 정해 두지 않으면 계산과 답이 저마다
 * 다르게 한다(조사 보고서 「분석 질문 자율 판단 에이전트」, 2026-10-20). 계획이 `compare-periods` 나
 * 「전후 비교」를 연산에 적었을 때만 붙는다.
 */
export function periodRule(requirements?: DataRequirements): string {
  if (!requirements?.operations.some((operation) => /compare-periods|전후\s*비교/.test(operation))) return '';
  return '- 기준일 전후 비교다. 기준일 당일은 「이후」에 넣는다. 기간마다 날짜 수·행 수·지표를 따로 내고,'
    + ' 지표를 무엇으로 셌는지 한 줄로 적어라. 한쪽 기간이 근무일 5일 미만이면 「아직 며칠치뿐이라 초기 신호」라고 적어라.'
    + ' 전후 차이를 원인으로 단정하지 마라.';
}

export async function makeCode(
  question: string,
  shapes: Shape[],
  chat: Chat,
  wantsFile = false,
  requirements?: DataRequirements,
  /** 뽑힌 스킬의 업무 규칙. 계획만 받고 계산이 못 받아 「거리 값이 있으면 모바일」로 셌다(운영, 2026-10-20). */
  rules = '',
): Promise<string> {
  const described = shapes
    .map((shape) => {
      const columns = shape.columns
        .map((column) => {
          const meaning = [column.text, columnMeaning(column)].filter(Boolean).join('; ');
          return meaning ? `${column.id}(${meaning})` : column.id;
        })
        .join(', ');
      const totals = shape.scalars?.length
        ? `\n  totals["${shape.label}"] — ${shape.scalars
          .map((s) => `${s.key}(${s.label}${s.blank ? ', 비어 있음 — 쓰지 말고 행에서 세라' : ''})`).join(', ')}`
        : '';
      const conditions = shape.conditions ? `\n  조회 조건 — ${shape.conditions}` : '';
      return `  rows["${shape.label}"] — ${shape.rowCount}행, 컬럼: ${columns}${conditions}${totals}`;
    })
    .join('\n');
  // 규칙 목록 안에 넣는다. 프롬프트 맨 끝에 붙이면 데이터 목록 아래에 떨어져
  // 규칙으로 읽히지 않는다. 스칼라가 없으면 한 글자도 달라지지 않는다.
  const ROWS_RULE = '- 데이터는 `rows` 라는 딕셔너리로 주어진다. 키는 아래 label 이고 값은 행 목록이다';
  // 워커 시계는 UTC 다 — 한국 이른 아침에 datetime.now() 가 어제를 냈다(중대결함 경과일, 2026-10-22).
  const dated = CODE_PROMPT.replace(ROWS_RULE, `${ROWS_RULE}
- 오늘은 ${localToday()} 이다. 오늘 날짜가 필요하면 datetime.now()·date.today() 대신 이 날짜를 써라`);
  const withScalars = shapes.some((shape) => shape.scalars?.length)
    ? dated.replace(
      ROWS_RULE,
      `${ROWS_RULE}\n- 화면이 이미 계산해 둔 값은 같은 키의 \`totals\` 로 주어진다. 행을 다시 합산하지 말고 그것을 써라.`
        + ' 단 「비어 있음」이라 적힌 칸은 화면이 채우지 못한 것이다 — 그 칸을 읽지 말고 행에서 직접 세거나 더해라.'
        + ' 행이 있는데 「자료가 없다」고 하지 마라',
    )
    : dated;
  const period = periodRule(requirements);
  const periodRuled = period ? withScalars.replace(ROWS_RULE, `${ROWS_RULE}
${period}`) : withScalars;
  const ruled = rules.trim() ? periodRuled.replace(ROWS_RULE, `${ROWS_RULE}
- **업무 규칙 — 반드시 따른다.** 아래와 어긋나게 세거나 가르지 마라:
${rules.trim()}`) : periodRuled;
  const contract = requirements
    ? JSON.stringify(requirements)
    : '명시되지 않음. 질문과 실제 컬럼만으로 판단한다.';
  const prompt = ruled.replace('{question}', question)
    .replace('{requirements}', contract)
    .replace('{shapes}', described)
    + (wantsFile ? FILE_PROMPT : '');
  const body = await chat(prompt, { step: 'compute' });
  return body.trim().replace(/^```(?:python)?\s*|\s*```$/g, '').trim();
}

export const ANSWER_PROMPT = `계산 결과를 직원에게 설명한다. 오늘은 {today} 다.

- 진중하고 친절하게. 형식적인 인사는 하지 마라
- 계산 결과에 없는 것을 지어내지 마라
- 숫자는 읽기 좋게 쓴다 (1,234,567)
- 읽는 사람은 회사 사무직이다. **우리 쪽 말을 쓰지 마라** — capability, 화면 경로(/w_...),
  컬럼 id, 상태 키, 계산 결과, 스킬 같은 말은 그 사람에게 아무 뜻이 없다.
  낼 수 없으면 "그 자료는 확인할 수 없습니다" 처럼 사람 말로 적는다
- 사람이 기간을 말하지 않았으면 어느 기간을 봤는지 첫 줄에 밝힌다 (예: "이번 달(8월) 기준입니다").
  묻지 않고 정한 것이라 그 사실을 알아야 다른 기간을 다시 물을 수 있다
- 계산 결과에 "판정기준" 이 있으면 무엇으로 셌는지 한 줄 밝힌다 (예: "경력 기록이 있는 분을 경력직으로
  셌습니다"). 우리가 정한 기준이라 그 사람이 보고 다르면 고쳐 물을 수 있어야 한다
- 연간 표는 열두 달이 다 들어 있어도 **아직 오지 않은 달**은 실적이 아니다. 오늘 이후의
  달을 감소·급락으로 읽지 마라. 필요하면 "아직 실적이 쌓이지 않은 기간" 이라고 적는다
- 지난 달과 견줄 때도 오늘이 속한 달은 아직 진행 중이다. 그 점을 밝히고 견준다
- **조회된 행이 하나도 없으면 업무 결론을 내지 마라.** "안 한 곳이 없습니다"·"전부 처리됐습니다"
  는 자료를 보고 한 말처럼 읽힌다. 없는 것은 자료다 — "그 조건으로는 자료가 없습니다"
  라고 적고, 짐작되는 까닭이 있으면 덧붙인다 (예: 그 달 계획이 아직 만들어지지 않았다)
- **물은 것은 본문에 적는다.** 표가 함께 나가도 마찬가지다 — "접수 건수와 완료 건수는
  아래 표에서 확인해 주세요" 는 물은 것에 답하지 않은 것이다. 물은 수치를 문장으로 먼저
  말하고, 그 다음에 나머지는 표를 보라고 안내한다
- 숫자만이 아니다. **어디·누가·어느 것인지 물었으면 그 이름을 본문에 적는다.**
  "다섯 곳 알려줘" 에 "순위별 현장명은 아래 표에서 확인해 주세요" 는 답이 아니다 —
  다섯이면 다섯을 이름과 값으로 짧게 적고, 그보다 많으면 위 몇 개를 적고 나머지를 표로 넘긴다
- **찾는 물음**(이름·번호로 "찾아줘", "있어?", "언제 했어?")에는 찾은 문서를 한 줄씩 적는다 —
  날짜·번호·이름·금액·상태처럼 그 문서를 가려낼 수 있는 것으로. 세 건 이하면 전부 적고 건수
  요약을 앞세우지 마라. 사람은 목록이 아니라 그 한 건을 찾고 있다
- 행이 하나도 없으면 **0 이라는 숫자도 적지 마라.** "매입 발생금액은 0원입니다",
  "모두 0원으로 집계되었습니다" 는 0원어치 거래가 있었다는 말로 읽힌다. 0 은 잰 값이
  아니라 **잴 것이 없었다**는 뜻이다 — 자료가 없다고만 적는다

${PLAIN_WRITING}

질문: {question}

계산 결과: {value}`;

/**
 * 답변 프롬프트에 실어도 되는 계산 결과의 크기 상한.
 *
 * 코드를 쓰는 것이 LLM 이므로 `result = rows` 라고 쓰면 조회한 행 전체가 이
 * 경로로 되돌아 나간다. 악의가 아니라 "데이터를 보여주려고" 그럴 수 있다.
 * 계산 결과는 요약이어야 하고 요약은 짧다. 넘으면 조용히 자르지 않고 던진다 —
 * 자르면 LLM 이 잘린 데이터를 사실로 읽는다.
 */
export const MAX_VALUE_CHARS = 2000;

function isTable(value: unknown): value is TableData {
  const table = value as TableData | null;
  return !!table
    && Array.isArray(table.columns)
    && table.columns.every((column) => typeof column === 'string')
    && Array.isArray(table.rows)
    && table.rows.every((row) => Array.isArray(row));
}

function usefulTable(table: TableData, requirements?: DataRequirements): boolean {
  if (!table.columns.length || !table.rows.length) return false;
  const meaningful = table.rows.filter((row) => row.some((cell) => (
    cell !== null && cell !== undefined && String(cell).trim() !== ''
  )));
  if (!meaningful.length) return false;
  if (table.columns.length > 1 && !meaningful.some((row) => row.filter((cell) => (
    cell !== null && cell !== undefined && String(cell).trim() !== ''
  )).length >= 2)) return false;
  const numericMeasure = (requirements?.measures ?? [])
    .some((measure) => /금액|매출|매입|수익|미수|일수|평균|합계|건수/.test(measure));
  if (!numericMeasure) return true;
  return meaningful.some((row) => row.some((cell) => (
    typeof cell === 'number' || /^-?[\d,]+(?:\.\d+)?$/.test(String(cell).trim())
  )));
}

function recordsTable(value: unknown): TableData | undefined {
  if (!Array.isArray(value) || !value.length
    || value.some((row) => !row || typeof row !== 'object' || Array.isArray(row))) return undefined;
  const records = value as Record<string, unknown>[];
  const columns = Array.from(new Set(records.flatMap((row) => Object.keys(row))));
  return { columns, rows: records.map((row) => columns.map((column) => row[column])) };
}

function derivedTable(
  shaped: Record<string, unknown>,
  requirements?: DataRequirements,
): TableData | undefined {
  const directRows = recordsTable(shaped.rows);
  if (directRows) return directRows;

  const groups = shaped.groups;
  if (groups && typeof groups === 'object' && !Array.isArray(groups)) {
    const dimensions = requirements?.dimensions ?? [];
    const entries = Object.entries(groups as Record<string, unknown>);
    const selected = entries.find(([label]) => dimensions.some((dimension) => (
      dimension.includes(label) || label.includes(dimension.replace(/명$/, ''))
    ))) ?? (entries.length === 1 ? entries[0] : undefined);
    const grouped = selected ? recordsTable(selected[1]) : undefined;
    if (grouped) {
      const measure = requirements?.measures?.[0]?.split('=')[0]?.trim();
      return {
        ...grouped,
        columns: grouped.columns.map((column) => (
          column === '구분' ? selected![0] : column === '인원' && measure ? measure : column
        )),
      };
    }
  }

  const named = Object.entries(shaped).find(([key, value]) => (
    key !== 'summary' && isTable(value)
    && (requirements?.dimensions ?? []).some((dimension) => (
      key.toLocaleLowerCase().includes(dimension.toLocaleLowerCase())
      || (dimension === '월' && /month/i.test(key))
    ))
  ));
  return named && isTable(named[1]) ? named[1] : undefined;
}

/**
 * 계산 결과를 답변용 요약과 파일용 표로 가른다.
 *
 * 표를 요청하지 않은 질문은 값 전체가 요약이다 — 예전과 같다.
 */
export function splitResult(
  value: unknown,
  requirements?: DataRequirements,
): { summary: unknown; table?: TableData } {
  const shaped = value as { summary?: unknown; table?: unknown } | null;
  if (shaped && typeof shaped === 'object' && 'summary' in shaped && isTable(shaped.table)
    && usefulTable(shaped.table, requirements)) {
    return { summary: shaped.summary, table: capTable(shaped.table) };
  }
  if (shaped && typeof shaped === 'object' && 'summary' in shaped) {
    const table = derivedTable(shaped as Record<string, unknown>, requirements);
    if (table && usefulTable(table, requirements)) {
      return { summary: shaped.summary, table: capTable(table) };
    }
    // 표가 없을 뿐, 요약은 모델이 준 그 자리에 있다. 다만 **요약 문장만 남기고 숫자를
    // 버리면 안 된다** — `{summary: "이번 달 지출결의서 건수와 금액", 건수: 1, 금액: 120000}`
    // 에서 문장만 넘겼더니 답변이 "수치가 제공되지 않아 안내할 수 없습니다" 로 나갔다.
    // 표로 만들 수 없는 값(행이 아니라 낱개 수치)도 답에는 실려야 한다.
    const { table: _dropped, ...rest } = shaped as Record<string, unknown>;
    return { summary: Object.keys(rest).length > 1 ? rest : shaped.summary };
  }
  // **`summary` 키 없이 수치와 표를 나란히 낸 결과.** 코드 프롬프트가 권하는 모양이다
  // (`{"접수": 4, "완료": 2, "table": …}`). 예전에는 이것을 통째로 요약으로 읽어 표의 행까지
  // 답변 프롬프트에 실었고, 2,000자에서 잘려 답이 「목록이 중간에서 끊겼다」고 썼다.
  if (shaped && typeof shaped === 'object' && !Array.isArray(shaped) && isTable(shaped.table)
    && usefulTable(shaped.table, requirements)) {
    const { table, ...rest } = shaped as Record<string, unknown>;
    return { summary: rest, table: capTable(table as TableData) };
  }
  return { summary: value };
}

/** 앞쪽 몇 건을 답에 적고 나머지는 표로 넘기는 경계. 코드 프롬프트의 「앞쪽 최대 10건」과 같다. */
export const LISTED_ROWS = 10;

/**
 * 요약이 상한을 넘었는가. 넘었으면 그 글자 수, 아니면 0.
 *
 * 실행 뒤에 재서 코드를 한 번 다시 쓰게 한다 — 실행 피드백으로 코드를 고치는 것이
 * 프롬프트로 미리 말리는 것보다 잘 듣는다(Data Interpreter, arXiv 2402.18679).
 */
export function summaryOverflow(value: unknown, requirements?: DataRequirements): number {
  const { summary } = splitResult(value, requirements);
  const chars = (JSON.stringify(summary) ?? '').length;
  return chars > MAX_VALUE_CHARS ? chars : 0;
}

/** 다시 쓰게 할 때 붙이는 실행 피드백. */
export function oversizedNote(chars: number): string {
  return `직전 계산의 summary 가 ${chars.toLocaleString('en-US')}자다(상한 ${MAX_VALUE_CHARS.toLocaleString('en-US')}자).`
    + ' 여러 행의 목록은 "table" 로 내고, summary 에는 건수·합계·그룹 집계·앞쪽 10건만 담아라.';
}

type ListSpot = { path: (string | number)[]; size: number; table: TableData; count: number };

/** 목록 하나를 표로 바꾼다. 행 객체·행 배열·낱개 값의 배열, 그리고 이름→값 짝이 많은 객체. */
function listAsTable(value: unknown, name: string): { table: TableData; count: number } | undefined {
  if (Array.isArray(value)) {
    if (value.length < 2) return undefined;
    const records = recordsTable(value);
    if (records) return { table: records, count: value.length };
    if (value.every((row) => Array.isArray(row))) {
      const width = Math.max(...value.map((row) => (row as unknown[]).length));
      return {
        table: {
          columns: Array.from({ length: width }, (_, at) => `${name} ${at + 1}`),
          rows: value as unknown[][],
        },
        count: value.length,
      };
    }
    if (value.every((cell) => cell === null || typeof cell !== 'object')) {
      return { table: { columns: [name], rows: value.map((cell) => [cell]) }, count: value.length };
    }
    return undefined;
  }
  if (value && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>);
    if (entries.length <= LISTED_ROWS) return undefined;
    if (!entries.every(([, cell]) => cell === null || typeof cell !== 'object')) return undefined;
    return { table: { columns: [name, '값'], rows: entries.map(([key, cell]) => [key, cell]) }, count: entries.length };
  }
  if (typeof value === 'string') {
    // 줄마다 한 건인 긴 글도 목록이다 — `"- 가나빌딩\n- 다라빌딩\n…"`.
    const lines = value.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
    if (lines.length <= LISTED_ROWS) return undefined;
    return { table: { columns: [name], rows: lines.map((line) => [line]) }, count: lines.length };
  }
  return undefined;
}

/** 요약 안에서 가장 큰 목록을 찾는다. 글자 수로 잰다 — 답변 프롬프트를 넘치게 하는 것이 그것이다. */
function largestList(value: unknown, path: (string | number)[] = [], name = '항목'): ListSpot | undefined {
  let best: ListSpot | undefined;
  const consider = (spot: ListSpot | undefined) => {
    if (spot && (!best || spot.size > best.size)) best = spot;
  };
  const here = listAsTable(value, name);
  if (here) consider({ path, size: (JSON.stringify(value) ?? '').length, ...here });
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    for (const [key, child] of Object.entries(value as Record<string, unknown>)) {
      consider(largestList(child, [...path, key], key));
    }
  }
  return best;
}

/**
 * 다시 써도 요약이 넘치면 **런타임이 정리한다.** 모델에게 한 번 더 맡기지 않는다.
 *
 * 요약 안의 가장 큰 목록을 표로 옮기고, 그 자리에는 건수만 남긴다. 이미 표가 있으면
 * 표는 손대지 않고 목록만 건수로 줄인다 — 모델이 고른 표가 질문에 더 맞다.
 * 넘친 채로 두면 답변이 앞 2,000자만 보고 「목록이 중간에서 끊겼다」고 쓴다
 * (「이번 달 점검 아직 안 한 현장 보여줘」, 2,035건, 실제 테넌트 2026-10-07).
 */
export function tidyOversized(value: unknown, requirements?: DataRequirements): unknown {
  const split = splitResult(value, requirements);
  let summary = split.summary;
  let table = split.table;
  for (let pass = 0; pass < 3 && (JSON.stringify(summary) ?? '').length > MAX_VALUE_CHARS; pass += 1) {
    const spot = largestList(summary);
    if (!spot) break;
    // 옮긴 표가 표로 쓸모없으면(답변 단계가 버린다) 표에 있다고 적지 않는다 — 앞쪽 몇 건만 남긴다.
    const movable = !table && usefulTable(spot.table, requirements);
    if (movable) table = capTable(spot.table);
    const total = spot.count.toLocaleString('en-US');
    const count = movable || table
      ? `${total}건(전체는 표에 있다)`
      : { 건수: `${total}건`, [`앞쪽 ${LISTED_ROWS}건`]: spot.table.rows.slice(0, LISTED_ROWS) };
    if (!spot.path.length) {
      summary = typeof count === 'string' ? { 건수: count } : count;
      continue;
    }
    const copy = JSON.parse(JSON.stringify(summary)) as Record<string, unknown>;
    let parent: Record<string, unknown> = copy;
    for (const key of spot.path.slice(0, -1)) parent = parent[key as string] as Record<string, unknown>;
    parent[String(spot.path[spot.path.length - 1])] = count;
    summary = copy;
  }
  return table ? { summary, table } : { summary };
}

/**
 * 요약이 상한을 넘어 앞부분만 실을 때 붙이는 표지.
 *
 * 예전 표지(`<<OutputTruncated>>`)를 받은 답변은 그것을 **자료가 잘렸다**로 읽어
 * 「제공된 목록이 중간에서 끊겨 전체 현장 목록은 확인할 수 없습니다」라고 썼다. 잘린 것은
 * 답변 프롬프트에 실은 부분이지 사용자의 자료가 아니다 — 그 뜻을 그대로 적는다.
 */
export const TRUNCATION_MARKER = '(요약이 길어 앞부분만 실었다. 이것은 자료가 잘린 것이 아니다 — "끊겼다"·"잘렸다"고 쓰지 마라)';
const TRUNCATION_WITH_TABLE = '(요약이 길어 앞부분만 실었다. 전체는 아래 표에 있다 — "끊겼다"·"잘렸다"고 쓰지 마라)';

export function summaryForPrompt(summary: unknown, hasTable = false): string {
  const serialized = JSON.stringify(summary) ?? 'null';
  if (serialized.length <= MAX_VALUE_CHARS) return serialized;
  return `${serialized.slice(0, MAX_VALUE_CHARS)}\n${hasTable ? TRUNCATION_WITH_TABLE : TRUNCATION_MARKER}`;
}

/**
 * 답변 프롬프트에 실을 표 미리보기.
 *
 * 표 전체를 실으면 계산을 브라우저 안에 둔 뜻이 없어진다. **앞 몇 줄만** 보인다 —
 * 사람이 물은 것(어디가 제일 많은지, 다섯 곳이 어디인지)은 대개 앞줄에 있다.
 */
const PREVIEW_ROWS = 8;
const PREVIEW_CELL = 40;

export function tablePreview(table: TableData): string {
  const cell = (value: unknown) => {
    const text = String(value ?? '').trim();
    return text.length > PREVIEW_CELL ? `${text.slice(0, PREVIEW_CELL)}…` : text;
  };
  const lines = [table.columns.join(' | ')];
  for (const row of table.rows.slice(0, PREVIEW_ROWS)) lines.push(row.map(cell).join(' | '));
  if (table.rows.length > PREVIEW_ROWS) {
    lines.push(`… 그 아래 ${table.rows.length - PREVIEW_ROWS}행 더`);
  }
  return ['표 앞부분:', ...lines].join('\n');
}

/**
 * 앞쪽 몇 건으로 다 못 적는 표의 답 형식.
 *
 * 패널은 표를 답 아래에 그리지 않는다 — 대신 이런 답에는 「엑셀로 받기」 단추가 붙는다
 * (`index.ts` 의 `answerFormats`). 그래서 「아래 표」가 아니라 그 단추로 안내한다.
 * 형식은 ChatGPT 데이터 분석의 답 모양을 따른다 — 답 문장, 따로 받는 전체 표, 후속 제안 하나.
 */
function bigTableNote(table: TableData): string {
  const total = (table.rows.length + (table.truncated ?? 0)).toLocaleString('en-US');
  return `

이 결과는 총 ${total}건이다. 전체 목록은 답 아래 「엑셀로 받기」 단추로 받을 수 있다 — 이것은 사실이다.
답은 이 순서로 쓴다:
1. 물은 핵심 수치(총 ${total}건 등)
2. 묶은 건수(그룹 집계)가 결과에 있으면 그 위 몇 개
3. 대표 5~10건을 이름과 함께
4. "전체 ${total}건은 아래 「엑셀로 받기」로 받으실 수 있습니다."
5. 표의 칸을 근거로 한 후속 제안 하나 (예: "담당자별로 나눠 볼까요?")
"끊겼다"·"잘렸다"·"일부만 받았다"·"전체는 확인할 수 없다"고 쓰지 마라 — 전체가 표에 있다.
"아래 표에서 확인해 주세요"라고도 쓰지 마라 — 화면에는 표가 그려지지 않는다. 4번의 단추로 안내한다.`;
}

export async function compose(
  question: string,
  value: unknown,
  code: string,
  plan: RetrievePlan,
  chat: Chat,
  streaming: { stream?: ChatStream; onDelta?: (text: string) => void } = {},
  empty = false,
  /**
   * 답에 꼭 반영해야 할, **우리가 확실히 아는** 사실들. 계산 결과만 보고는 모르는 것이다
   * — 예: 읽은 표가 목록 전체가 아니라 선택된 한 건의 상세라는 것.
   */
  facts: string[] = [],
  /**
   * 답이 볼 요약과 표. 없으면 계산 결과를 그대로 본다. 연차 촉진 명단은 표 앞부분을 잔여 많은 순으로 보인다(`index.ts`의
   * `rankedView`). 답의 결과 기록과 엑셀 표는 계산 결과 그대로다.
   */
  view?: { summary: unknown; table?: TableData },
): Promise<Answer> {
  const { summary, table } = view ?? splitResult(value, plan.requirements);
  // 표는 요약과 갈라 답변 **아래에 그대로 나간다.** 그 사실을 알리지 않으면 요약만
  // 보고 "현장별 금액은 확인할 수 없습니다" 라고 쓴다 — 바로 아래에 스물여덟 행짜리
  // 순위표가 붙어 있는데도. 실제로 그렇게 나갔다.
  const tableNote = table
    ? `

상세 표(${table.columns.join(', ')} · ${table.rows.length}행)가 이 답변 아래에`
      + ' 함께 나간다. 그 내용을 "없다" 거나 "확인할 수 없다" 고 쓰지 마라.'
      + ' 요약에서 짚을 것만 짚고 나머지는 표를 보라고 안내한다.'
      // **앞부분은 보여 준다.** 이름을 물었는데 표만 넘기면 답변은 그 이름을 못 본다 —
      // `상위 5곳의 현장명은 아래 표에` 라고만 쓰고 끝났다. 이름을 적으려면 이름이 있어야 한다.
      + `
${tablePreview(table)}`
      + (table.rows.length > LISTED_ROWS ? bigTableNote(table) : '')
    : '';
  const prompt = ANSWER_PROMPT
    .replace('{today}', localToday())
    .replace('{question}', question)
    // 행이 하나도 없으면 **숫자를 아예 넘기지 않는다.** 합계 코드는 빈 목록에서
    // 0 을 내고, 그 0 을 받은 답변은 `매입 발생금액은 0원입니다` 라고 적는다 —
    // 0원어치 거래가 있었다는 말로 읽힌다. 프롬프트로 말리는 것만으로는 안 됐다.
    .replace('{value}', empty
      ? '조회된 행이 하나도 없다. 낼 수 있는 숫자가 없다.'
      : summaryForPrompt(summary, Boolean(table)) + tableNote)
    + (facts.length ? `\n\n반드시 반영할 사실:\n${facts.map((one) => `- ${one}`).join('\n')}` : '');
  // 흘리는 곳은 여기뿐이다. 계획과 계산 코드는 JSON·파이썬이라 반쯤 온 것으로
  // 할 수 있는 일이 없다.
  // 흘려 받기가 죽어도 답까지 버리지 않는다. 조회와 계산은 이미 끝났고 그 시간이
  // 이 질문의 대부분이다 — 마지막 한 번을 못 불러서 통째로 잃는 것은 아깝다.
  // 실제로 스트리밍만 502 로 죽는 동안 멀쩡한 계산 결과가 계속 버려졌다.
  let text: string;
  if (streaming.stream) {
    try {
      text = await streaming.stream(prompt, streaming.onDelta ?? (() => {}), { step: 'answer' });
    } catch {
      text = await chat(prompt, { step: 'answer' });
    }
  } else {
    text = await chat(prompt, { step: 'answer' });
  }
  return answerFrom(question, text, value, code, plan);
}

/**
 * 답 글에 출처·표·결과 기록을 붙여 답으로 만든다. 전문가의 작성 담당이 쓴 문서도 이것으로 답이 된다.
 */
export function answerFrom(question: string, text: string, value: unknown, code: string, plan: RetrievePlan): Answer {
  const { summary, table } = splitResult(value, plan.requirements);
  // 실행한 코드를 함께 낸다. 숫자만 주는 것보다 검증 가능성이 높다 —
  // 어디서 틀렸는지 짚을 수 있다.
  const sources = plan.retrievals.map((retrieval) => {
    const conditions = Object.entries(retrieval.conditions)
      // 빈 조건은 걸지 않은 것이다. 적어 두면 `항목 , 세액공제 , 부서 ,` 처럼
      // 아무 뜻 없는 꼬리가 답변 끝에 붙는다.
      .filter(([, value_]) => String(value_ ?? '').trim())
      .map(([key, value_]) => `${key}=${value_}`)
      .join(', ');
    return conditions ? `${retrieval.route} (${conditions})` : retrieval.route;
  });
  const createdAt = new Date().toISOString();
  const result: ResultSnapshot = {
    id: `result-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    question, summary, table, sources, createdAt,
  };
  return { text: text.trim(), code, sources, routes: plan.retrievals.map((r) => r.route), table, result };
}
